import { EditorialPageLayout } from '@/components/seo/EditorialPageLayout';

export default function WhatIsALivingAnnouncement() {
  return (
    <EditorialPageLayout
      path="/what-is-a-living-announcement"
      ogImage="/og/what-is-a-living-announcement.jpg"
      title="What Is a Living Announcement? — Say It NOW"
      metaDescription="A Living Announcement is a heartfelt digital message shared with someone while they're still alive to receive it. Learn what they are, why they matter, and how to create one."
      eyebrow="A new kind of message"
      h1="What is a Living Announcement?"
      lede="A Living Announcement is the message you would have written at a funeral — delivered while they can still read it, hear it, hold it."
      articleSchema={{
        headline: 'What is a Living Announcement?',
        description:
          'A Living Announcement is a heartfelt digital message shared with someone while they are still alive — combining words, photos, and presence into a lasting keepsake.',
      }}
      related={[
        { label: 'Why Say It Now exists', href: '/why-say-it-now-exists', hint: 'The quiet idea behind the platform.' },
        { label: 'Digital gratitude', href: '/digital-gratitude', hint: 'How saying thank you, on purpose, changes a relationship.' },
        { label: 'Heartfelt message to Mom', href: '/heartfelt-message-to-mom', hint: 'A guide for the words you keep meaning to send.' },
      ]}
    >
      <p>
        Most of the most important things we will ever say about someone get said
        after they are gone. In eulogies. In cards left on a casket. In group
        threads after the funeral, where everyone suddenly remembers what they
        loved.
      </p>

      <p>
        A <strong>Living Announcement</strong> is a quiet act of rebellion against
        that pattern. It is a love letter, a thank you, a small confession,
        an apology, a celebration — built into something beautiful and sent while
        the person you are writing about is still here to receive it.
      </p>

      <h2>It is not a card. It is not a text.</h2>
      <p>
        A greeting card is generic. A text disappears in a feed. A Living
        Announcement is designed to be opened slowly. It has a title. A photo.
        A signature. A reveal. It feels like something — because it is something.
      </p>

      <p>
        Each one becomes a private, permanent link. They can return to it on
        a hard day. They can show their daughter. They can read it again at
        eighty-three.
      </p>

      <h2>What a Living Announcement usually contains</h2>
      <ul>
        <li>The person you are writing to — and your relationship to them.</li>
        <li>A photograph that means something to both of you.</li>
        <li>The words you have not quite been able to say out loud.</li>
        <li>A premium template that holds those words with weight.</li>
      </ul>

      <h2>Why people make them</h2>
      <p>
        Not because they are scared someone is going to die. Because they are
        tired of waiting for the right moment. Birthdays come and go. Holidays
        get crowded. A Tuesday in May turns out to be a fine day to tell your
        mother she made you who you are.
      </p>

      <blockquote>
        Celebrate people while they're alive. Not as a slogan — as a small,
        repeatable practice.
      </blockquote>

      <h2>How to make your first one</h2>
      <p>
        Pick one person. Write the first sentence badly. Let{' '}
        <a href="/">JacKey</a> ask you a question that gets at what you actually
        mean. Choose a template. Send it. The whole thing takes less time than
        watching an episode of something you have already seen.
      </p>
    </EditorialPageLayout>
  );
}
